import { useCallback, useEffect, useState } from "react";
import { TrendingDownIcon, ViewGridAddIcon } from "@heroicons/react/solid";

import SubScale from "./SubScale";
import Percentile from "./Percentile";

function SubjectiveCognitiveDecline(props) {
    const { score, userName } = props;

    const [colour, setColour] = useState("text-blue-gray");

    const handleSetColour = useCallback(async () => {
        setTimeout(() => {
            setColour(
                score?.reportDetails.overallScore <= 33
                    ? "green"
                    : score?.reportDetails.overallScore >= 34 && score?.reportDetails.overallScore < 66
                    ? "amber"
                    : "red"
            );
        }, 100);
    }, [score?.reportDetails.overallScore]);

    useEffect(() => {
        handleSetColour();
    }, [handleSetColour, score.percentile]);

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-4">
                    <div className="text-primary-700 text-xl font-semibold flex justify-between">
                        {score?.reportName}
                        <TrendingDownIcon className="h-6 w-6" />
                    </div>
                    <div className="flex flex-col gap-2 bg-white rounded-xl p-4 shadow-sm">
                        <div className="text-primary-600 text-lg font-semibold">Description</div>
                        <p className="text-blue-gray-500">{score?.reportDescription}</p>
                    </div>
                </div>
                <div className="flex flex-col xl:flex-row gap-4 col-span-2">
                    <div className="flex flex-col gap-4 bg-white rounded-xl p-4 shadow-sm">
                        <div className="text-primary-600 text-lg font-semibold">Client details</div>
                        <div className="flex flex-row flex-wrap gap-x-6 gap-y-4">
                            <div className="col-span-2 flex flex-row gap-2 w-max">
                                <label className="font-medium text-indigo-900">Client:</label>
                                <div className="text-blue-gray-500 break-normal	">{score?.reportDetails.clientName}</div>
                            </div>
                            <div className="flex flex-row gap-2 w-max">
                                <label className="font-medium text-indigo-900">Gender:</label>
                                <div className="text-blue-gray-500 break-normal	">{score?.reportDetails.gender}</div>
                            </div>
                            <div className="flex flex-row gap-2 w-max">
                                <label className="font-medium text-indigo-900">ID:</label>
                                <div className="text-blue-gray-500 break-normal	">{score?.reportDetails.id}</div>
                            </div>
                            <div className="flex flex-row gap-2 w-max">
                                <label className="font-medium text-indigo-900">Age:</label>
                                <div className="text-blue-gray-500 break-normal	">{score?.reportDetails.age}</div>
                            </div>
                            <div className="flex flex-row gap-2 w-max">
                                <label className="font-medium text-indigo-900">Date of birth:</label>
                                <div className="text-blue-gray-500 break-normal	">{score?.reportDetails.dob}</div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-4 bg-white rounded-xl p-4 shadow-sm">
                        <div className="text-primary-600 text-lg font-semibold">Admin details</div>
                        <div className="flex flex-row flex-wrap gap-x-6 gap-y-4">
                            <div className="flex flex-row gap-2 w-max">
                                <label className="font-medium text-indigo-900">Health practitioner:</label>
                                <div className="text-blue-gray-500 break-normal	">
                                    {score?.reportDetails.healthPractitioner}
                                </div>
                            </div>
                            <div className="flex flex-row gap-2 w-max">
                                <label className="font-medium text-indigo-900">Admin date:</label>
                                <div className="text-blue-gray-500 break-normal	">{score?.reportDetails.adminDate}</div>
                            </div>
                            <div className="flex flex-row gap-2 w-max">
                                <label className="font-medium text-indigo-900">Previous admin date:</label>
                                <div className="text-blue-gray-500 break-normal	">
                                    {score?.reportDetails.previousAdminDate}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col gap-2 bg-white rounded-xl p-4 shadow-sm">
                    <div className="text-primary-600 text-lg font-semibold">Overall score</div>
                    <div className="flex flex-col md:flex-row gap-4">
                        <p className="text-blue-gray-500">
                            {score?.reportFields.find((scale) => scale.name === "SCD Total Score").description}
                        </p>
                        <Percentile
                            size={"large"}
                            percentile={
                                score?.reportFields.find((scale) => scale.name === "SCD Total Score").percentile
                            }
                            maxScore={91}
                            colour={colour}
                        />
                    </div>
                </div>
            </div>
            <div>
                <div className="text-xl text-primary-700 font-semibold flex flex-row items-center">
                    <ViewGridAddIcon className="h-6 w-5 mr-1.5" />
                    Detailed Interpretation
                </div>
                <div className="grid grid-flow-row gap-8 mt-4">
                    <div className="flex flex-col gap-4">
                        {score?.reportFields.map(
                            (scale, index) =>
                                scale.percentile > 33 &&
                                scale.name !== "SCD Total Score" && (
                                    <div className={"bg-white shadow-sm p-4 rounded-xl"} key={index}>
                                        <SubScale
                                            name={scale.name}
                                            userName={userName}
                                            description={scale.description}
                                            score={scale.percentile}
                                        />
                                    </div>
                                )
                        )}
                    </div>
                </div>
            </div>
            <div className="flex flex-col gap-6">
                {score?.page2.map((section, index) => (
                    <div className="flex flex-col gap-4 " key={index}>
                        <div className="text-xl text-primary-700 font-semibold flex flex-row items-center">
                            {section.title}
                        </div>
                        <div className="flex flex-col gap-4">
                            <div className={"flex flex-col gap-4 bg-white shadow-sm p-4 rounded-xl text-blue-gray-500"}>
                                {section.paragraphs.map((paragraph, index) => (
                                    <p className={section.title === "References" && "text-xs"} key={index}>
                                        {paragraph}
                                    </p>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default SubjectiveCognitiveDecline;
